import { Col, Row, Tag, Typography } from "antd";
import React from "react";
import DetailItem from "../../../components/DetailItem";
import ListingImagePreview from "../../../components/ListingImagePreview";
import SellOrRentComponent from "../../../components/SellOrRentComponent";
import { formatUcWords } from "../../../helpers/format";
import { priceFormat } from "../../../helpers/priceFormat";
import { EListingSellOrRent } from "../../../types/listing.type";
import { EProfileMainEmail } from "../../../types/profile.type";
import { StepProps } from "../Detail";
import MapsPickerComponent from "../../listing/CreateComponents/MapsPickerComponent";

interface Props extends StepProps {
	isMinimal?: boolean;
	hideNavigation?: boolean;
	isHideListingCode?: boolean;
	hasListingPortal?: boolean;
}
const { Title } = Typography;
const DetailListingInformation: React.FC<Props> = ({ listing, isMinimal, isHideListingCode }) => {
	return (
		<React.Fragment>
			{!isMinimal && (
				<Row
					gutter={[24, 0]}
					className="detail-bordered"
				>
					<Col
						span={24}
						className="no-border"
					>
						<Title level={4}>Agent Information</Title>
					</Col>
					<Col
						xs={{ span: 24 }}
						md={{ span: 12 }}
					>
						<DetailItem
							label="Agent Name"
							value={listing?.user?.nickName?.toUpperCase()}
						/>
					</Col>
					<Col
						xs={{ span: 24 }}
						md={{ span: 12 }}
					>
						<DetailItem
							label="Contact Agent"
							value={listing?.profile?.firstPhoneNumber}
						/>
					</Col>

					<Col
						xs={{ span: 24 }}
						md={{ span: 12 }}
					>
						<DetailItem
							label="Email Agent"
							value={
								listing?.profile?.mainEmail === EProfileMainEmail.REMAX
									? listing.profile.remaxEmail
									: listing?.profile?.privateEmail
							}
						/>
					</Col>
					<Col
						xs={{ span: 24 }}
						md={{ span: 12 }}
					>
						<DetailItem
							label="Office"
							value={listing?.office?.officeName}
						/>
					</Col>
				</Row>
			)}

			{!isHideListingCode && (
				<Row
					justify="space-between"
					style={{ alignItems: "center", paddingTop: 20 }}
				>
					<Col>
						<Title level={4}>Listing Information</Title>
					</Col>
					<Col>
						{listing?.listingRenewHistory && listing.listingRenewHistory.length > 0 ? (
							<Tag color="processing">Renew Listing</Tag>
						) : (
							<></>
						)}
						{listing?.listingTransferHistory && listing.listingTransferHistory.length > 0 ? (
							<Tag color="orange">Receive Listing</Tag>
						) : (
							<></>
						)}
					</Col>
				</Row>
			)}

			<Row
				gutter={[24, 0]}
				className="detail-bordered"
			>
				{!isHideListingCode && (
					<Col
						span={24}
						className="no-border"
					>
						<DetailItem
							label="Listing ID"
							value={listing?.listingCode}
						/>
					</Col>
				)}

				<Col
					span={24}
					className="no-border"
				>
					<ListingImagePreview photos={listing?.photos} />
				</Col>

				<Col span={24}>
					<DetailItem
						label="URL Video Listing (YouTube)"
						value={listing?.videoUrl?.[0]}
						renderValue={(url) => (
							<a
								href={url.toString()}
								target="_blank"
								rel="noreferrer"
							>
								{url}
							</a>
						)}
					/>
				</Col>
				<Col span={24}>
					<DetailItem
						label="Title"
						value={listing?.title}
					/>
				</Col>

				<Col span={24}>
					<DetailItem label="Description">
						<div
							dangerouslySetInnerHTML={{
								__html: listing?.description ?? "",
							}}
							className="listing-description"
						/>
					</DetailItem>
				</Col>

				<Col
					xs={{ span: 24 }}
					md={{ span: 12 }}
				>
					<DetailItem
						label="Country"
						value={listing?.country}
					/>
				</Col>
				<Col
					xs={{ span: 24 }}
					md={{ span: 12 }}
				>
					<DetailItem
						label="Province"
						value={listing?.province}
					/>
				</Col>

				<Col
					span={24}
					className="no-border"
				>
					<Title level={4}>Specification Listing</Title>
				</Col>

				<Col
					xs={{ span: 24 }}
					md={{ span: 12 }}
				>
					<DetailItem label="Sell/Rent">
						<SellOrRentComponent value={listing?.sellOrRent} />
					</DetailItem>
				</Col>
				<Col
					xs={{ span: 12 }}
					md={{ span: 6 }}
				>
					<DetailItem
						label="Listing Type"
						value={listing?.listingType ? formatUcWords(listing.listingType) : ""}
					/>
				</Col>
				<Col
					xs={{ span: 12 }}
					md={{ span: 6 }}
				>
					<DetailItem
						label="Property Category"
						value={listing?.listingPart?.name}
					/>
				</Col>

				{listing?.sellOrRent === EListingSellOrRent.RENT && (
					<React.Fragment>
						<Col
							xs={{ span: 24 }}
							md={{ span: 12 }}
							lg={{ span: 8 }}
						>
							<DetailItem
								label="Daily Rent"
								value={listing?.additionalPriceFreq?.daily}
								renderValue={priceFormat}
							/>
						</Col>
						<Col
							xs={{ span: 24 }}
							md={{ span: 12 }}
							lg={{ span: 8 }}
						>
							<DetailItem
								label="Monthly Rent"
								value={listing?.additionalPriceFreq?.monthly}
								renderValue={priceFormat}
							/>
						</Col>
						<Col
							xs={{ span: 24 }}
							md={{ span: 24 }}
							lg={{ span: 8 }}
						>
							<DetailItem
								label="Yearly Rent"
								value={listing?.additionalPriceFreq?.yearly}
								renderValue={priceFormat}
							/>
						</Col>
					</React.Fragment>
				)}

				<Col
					xs={{ span: 24 }}
					md={{ span: 12 }}
				>
					<DetailItem
						label="Property Type"
						value={listing?.listingPropertyType?.name}
					/>
				</Col>
				<Col
					xs={{ span: 24 }}
					md={{ span: 12 }}
				>
					<DetailItem
						label="Certificate Type"
						value={listing?.listingCertificateType?.name}
					/>
				</Col>

				<Col
					xs={{ span: 24 }}
					md={{ span: 12 }}
				>
					<DetailItem
						label="Building Size"
						value={listing?.buildingArea}
						renderValue={(v) => (
							<>
								{v}m<sup>2</sup>
							</>
						)}
					/>
				</Col>
				<Col
					xs={{ span: 24 }}
					md={{ span: 12 }}
				>
					<DetailItem
						label="Land Size"
						value={listing?.landArea}
						renderValue={(v) => (
							<>
								{v}m<sup>2</sup>
							</>
						)}
					/>
				</Col>

				<Col
					xs={{ span: 24 }}
					md={{ span: 12 }}
				>
					<DetailItem
						label="Building Is Facing"
						value={listing?.buildingFace ? formatUcWords(listing.buildingFace) : ""}
					/>
				</Col>
				<Col
					xs={{ span: 24 }}
					md={{ span: 12 }}
				>
					<DetailItem
						label="Number Of Floors"
						value={listing?.numberOfFloor}
					/>
				</Col>

				<Col
					xs={{ span: 24 }}
					md={{ span: 12 }}
				>
					<DetailItem
						label="Bedroom"
						value={listing?.mainBedroom}
					/>
				</Col>
				<Col
					xs={{ span: 24 }}
					md={{ span: 12 }}
				>
					<DetailItem
						label="Bathroom"
						value={listing?.mainBathroom}
					/>
				</Col>

				<Col
					xs={{ span: 24 }}
					md={{ span: 12 }}
				>
					<DetailItem
						label="Housekeeper Bedroom"
						value={listing?.housekeepBedroom}
					/>
				</Col>
				<Col
					xs={{ span: 24 }}
					md={{ span: 12 }}
				>
					<DetailItem
						label="Housekeeper Bathroom"
						value={listing?.housekeepBathroom}
					/>
				</Col>

				<Col span={24}>
					<DetailItem label="Facility">
						{listing?.facilities
							?.filter((item) => item.total > 0)
							.map((item) => {
								return item.facilityName + " (" + item.total + ")";
							})
							.join(", ")}
					</DetailItem>
				</Col>

				<Col span={24}>
					<DetailItem
						label="Price"
						value={priceFormat(listing?.price)}
					/>
				</Col>

				<Col span={24}>
					<DetailItem
						label="PDF Orientation"
						value={listing?.pdfOrientation}
						renderValue={(v) => formatUcWords(v as string)}
					/>
				</Col>
			</Row>
		</React.Fragment>
	);
};
export default DetailListingInformation;
